import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { getButtonClass } from '../../../../components/popup/popupUtils';
import CustomSelect from '../../../../components/widgets/CustomSelect';
import { sanitizeSvg } from '../../../../assets/utils/comm/sanitize';

/**
 * 프로그램 폼 팝업 컴포넌트
 * 공통 팝업 시스템에서 사용하는 프로그램 등록/수정 폼
 */
const ProgramFormPopup = ({ config, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    progrmNm: '',
    url: '',
    progrmDc: '',
    useAt: 'Y',
    pageType: 'CMS',
    progrmFileNm: '',
    menuIcon: ''
  });
  const [validated, setValidated] = useState(false);

  // 초기 데이터 설정
  useEffect(() => {
    if (config?.initialData) {
      setFormData(config.initialData);
    }
  }, [config?.initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // menuIcon의 경우 입력값을 그대로 저장 (이미 문자열 형태)
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // SVG 태그 문자열을 실제 HTML로 변환하는 헬퍼 함수
  const parseSvgString = (svgString) => {
    if (!svgString || !svgString.trim()) return null;

    try {
      let decoded = svgString.trim();

      // 1. JSON 문자열 형태인지 확인 (따옴표로 시작하고 끝나는 경우)
      if ((decoded.startsWith('"') && decoded.endsWith('"')) ||
          (decoded.startsWith("'") && decoded.endsWith("'"))) {
        try {
          // JSON.parse를 시도하여 문자열을 파싱
          decoded = JSON.parse(decoded);
        } catch (jsonError) {
          // JSON 파싱 실패 시 따옴표만 제거
          decoded = decoded.slice(1, -1);
        }
      }

      // 2. HTML 이스케이프된 문자열 디코딩
      // &lt; -> <, &gt; -> >, &quot; -> ", &#39; -> ', &amp; -> &
      decoded = decoded
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, '&');

      // 3. 백슬래시 이스케이프 처리 (JSON 문자열에서 사용)
      decoded = decoded
        .replace(/\\"/g, '"')
        .replace(/\\'/g, "'")
        .replace(/\\\\/g, '\\')
        .replace(/\\n/g, '\n')
        .replace(/\\t/g, '\t')
        .replace(/\\r/g, '\r');

      // 4. SVG 태그인지 확인
      const trimmed = decoded.trim();
      if (trimmed.startsWith('<svg') || trimmed.startsWith('<SVG')) {
        return decoded;
      }

      // 5. 이미 디코딩된 경우 그대로 반환
      return svgString;
    } catch (error) {
      console.error('SVG parse error:', error);
      return null;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const form = e.currentTarget;
    if (!form.checkValidity()) {
      setValidated(true);
      return;
    }

    // 필수 항목 검증
    if (!formData.progrmNm || !formData.url) {
      setValidated(true);
      alert(t('popup.programFormPopup.alertRequiredNameUrl'));
      return;
    }

    // initialData에서 uid가 있으면 추가
    if (config?.initialData?.uid) {
      formData.uid = config.initialData.uid;
    }
    onClose(true, { data: formData });
  };

  const handleLeftButtonClick = () => {
    onClose(false);
  };

  const handleRightButtonClick = () => {
    const form = document.querySelector('#program-form-popup form');
    if (form) {
      form.requestSubmit();
    }
  };

  return (
    <PopupWrapper
      config={config}
      onClose={onClose}
      displayType={config?.displayType || 'modal'}
      className="popup-program-form"
      id="program-form-popup"
    >
      <div className="main-popup-body">
        <div className="main-popup-form">
          <form
            className={`needs-validation ${validated ? 'was-validated' : ''}`}
            noValidate
            onSubmit={handleSubmit}
          >
        <div className="row">
          <div className="single-input-item">
            <label htmlFor="program-progrmNm" className="required">
              {t('popup.programFormPopup.programName')}<span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="program-progrmNm"
              name="progrmNm"
              value={formData.progrmNm || ''}
              onChange={handleChange}
              required
            />
            <div className="invalid-feedback">{t('popup.programFormPopup.requiredField')}</div>
          </div>

          <div className="single-input-item">
            <label htmlFor="program-progrmDc">{t('popup.programFormPopup.description')}</label>
            <textarea
              className="form-control"
              id="program-progrmDc"
              name="progrmDc"
              value={formData.progrmDc || ''}
              onChange={handleChange}
              rows="3"
            />
          </div>

          {/* URL, 페이지 타입, 프로그램 파일명, 사용 여부를 2컬럼으로 구성 */}
          <div className="col-12 col-md-6">
            <div className="single-input-item">
              <label htmlFor="program-url" className="required">
                {t('popup.programFormPopup.url')}<span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="program-url"
                name="url"
                value={formData.url || ''}
                onChange={handleChange}
                required
              />
              <div className="invalid-feedback">{t('popup.programFormPopup.requiredField')}</div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="single-input-item">
              <label htmlFor="program-pageType">{t('popup.programFormPopup.pageType')}</label>
              <input
                type="text"
                className="form-control"
                id="program-pageType"
                name="pageType"
                value={formData.pageType || ''}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="single-input-item">
              <label htmlFor="program-progrmFileNm">{t('popup.programFormPopup.programFileName')}</label>
              <input
                type="text"
                className="form-control"
                id="program-progrmFileNm"
                name="progrmFileNm"
                value={formData.progrmFileNm || ''}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="single-input-item">
              <label htmlFor="program-useAt">{t('popup.programFormPopup.useYn')}</label>
              <CustomSelect
                inputId="program-useAt"
                name="useAt"
                value={formData.useAt || 'Y'}
                onChange={(v) => handleChange({ target: { name: 'useAt', value: v } })}
                isSearchable={false}
                isClearable={false}
                options={[
                  { value: 'Y', label: 'Y' },
                  { value: 'N', label: 'N' },
                ]}
              />
            </div>
          </div>

          {/* 메뉴 아이콘 (SVG 태그) */}
          <div className="single-input-item">
            <label htmlFor="program-menuIcon">{t('popup.programFormPopup.menuIconSvg')}</label>
            <div className="row g-3">
              {/* 텍스트 입력 영역 (4/5 비율) */}
              <div className="col-12 col-md-8">
                <textarea
                  className="form-control"
                  id="program-menuIcon"
                  name="menuIcon"
                  value={formData.menuIcon || ''}
                  onChange={handleChange}
                  rows="5"
                  placeholder={t('popup.programFormPopup.svgPlaceholder')}
                  style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                />
              </div>
              {/* 미리보기 영역 (1/5 비율) */}
              <div className="col-12 col-md-4">
                <div
                  style={{
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    minHeight: '120px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}
                >
                  <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '0.5rem', fontWeight: '500' }}>
                    {t('popup.programFormPopup.preview')}
                  </div>
                  {formData.menuIcon ? (() => {
                    const parsedSvg = parseSvgString(formData.menuIcon);
                    const safeSvg = parsedSvg ? sanitizeSvg(parsedSvg) : '';
                    return parsedSvg ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          minHeight: '60px'
                        }}
                        dangerouslySetInnerHTML={{ __html: safeSvg }}
                      />
                    ) : (
                      <div style={{ fontSize: '0.75rem', color: '#dc3545', textAlign: 'center' }}>
                        {t('popup.programFormPopup.invalidSvg')}
                      </div>
                    );
                  })() : (
                    <div style={{ fontSize: '0.75rem', color: '#adb5bd', textAlign: 'center' }}>
                      {t('popup.programFormPopup.enterSvgForPreview1')}<br />{t('popup.programFormPopup.enterSvgForPreview2')}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <small className="text-muted" style={{ fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>
              {t('popup.programFormPopup.svgHelp')}
            </small>
          </div>
        </div>
      </form>
        </div>
      </div>
      <div className="main-popup-footer d-flex justify-content-between">
        {config?.leftButton && (
          <button
            type="button"
            className={`btn main-popup-left-btn ${getButtonClass(config.leftButton.color || 'btn-secondary')}`}
            onClick={handleLeftButtonClick}
          >
            {config.leftButton.text || t('popup.common.cancel')}
          </button>
        )}
        {config?.rightButton && (
          <button
            type="button"
            className={`btn main-popup-right-btn ${getButtonClass(config.rightButton.color || 'btn-primary')}`}
            onClick={handleRightButtonClick}
          >
            {config.rightButton.text || t('popup.common.save')}
          </button>
        )}
      </div>
    </PopupWrapper>
  );
};

export default ProgramFormPopup;

